import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import {
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import {
  AppliedCoupon,
  Subscription,
  PricingBreakdown,
} from "../../types/plans";
import { useCouponValidation } from "../../api/hooks/useCoupon";
import { calculatePricing } from "../../api/services/couponService";
import { useAuth } from "../../context/AuthContext";
import { useCreateSubscription } from "../../api/hooks/usePayment";
import { CouponFormData } from "../checkout/CouponForm";
import { PaymentCard } from "./PaymentCard";
import { TotalDisplay } from "./TotalDisplay";
import { SubmitButton } from "./SubmitButton";
import { CouponForm } from "../checkout/CouponForm";
import { AppliedCouponDisplay } from "../checkout/AppliedCouponDisplay";
import { SetupIntentData } from "../../api";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY!);

interface IntegratedPaymentFormProps {
  selectedPlan: Subscription;
  paymentIntent: SetupIntentData;
}

function PaymentFormContent({
  selectedPlan,
  paymentIntent,
}: IntegratedPaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { account } = useAuth();
  const createSubscriptionMutation = useCreateSubscription();
  const couponValidation = useCouponValidation();

  const [appliedCoupon, setAppliedCoupon] = useState<AppliedCoupon | null>(
    null
  );
  const [pricingBreakdown, setPricingBreakdown] =
    useState<PricingBreakdown | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updatePricing = () => {
    if (!selectedPlan) return;
    const breakdown = calculatePricing({
      planPrice: selectedPlan.price.amount || 0,
      planDiscount: 0,
      appliedCoupon,
    });
    setPricingBreakdown(breakdown);
  };

  useEffect(() => {
    updatePricing();
  }, [selectedPlan, appliedCoupon]);

  const handleCouponSubmit = async (data: CouponFormData) => {
    try {
      setError(null);
      if (!selectedPlan) throw new Error("No plan selected");

      const result = await couponValidation.mutateAsync({
        couponCode: data.code,
        amount: selectedPlan.price.amount || 0,
      });

      if (!result.data) throw new Error("Invalid coupon code");

      setAppliedCoupon({
        couponId: result.data.couponId,
        code: result.data.code,
        discount: result.data.discountAmount,
        type: result.data.discountType,
        discountValue: result.data.discountValue,
        description: result.data.description,
        originalAmount: result.data.originalAmount,
        finalAmount: result.data.finalAmount,
      });
    } catch (error) {
      setError("Failed to validate coupon");
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements || !paymentIntent) return;

    setIsProcessing(true);
    setError(null);

    try {
      const cardNumberElement = elements.getElement(CardNumberElement);
      if (!cardNumberElement) throw new Error("Card element not found");

      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        paymentIntent.clientSecret,
        { payment_method: { card: cardNumberElement } }
      );

      if (stripeError) throw new Error(stripeError.message || "Payment failed");

      if (setupIntent.status === "succeeded") {
        const subscriptionData = await createSubscriptionMutation.mutateAsync({
          planId: selectedPlan.priceId,
          amount: pricingBreakdown?.total || selectedPlan.price.amount || 0,
          currency: selectedPlan.price.currency,
          couponId: appliedCoupon?.couponId,
          customerEmail: account?.email,
          customerName: account?.name || "",
        });

        if (subscriptionData.requiresPaymentConfirmation) {
          const { error: confirmError, paymentIntent: confirmedPaymentIntent } =
            await stripe.confirmCardPayment(subscriptionData.clientSecret!, {
              payment_method: { card: cardNumberElement },
            });

          if (confirmError)
            throw new Error(confirmError.message || "Payment failed");
          if (confirmedPaymentIntent.status !== "succeeded")
            throw new Error("Payment confirmation failed");
        }

        navigate("/payment-success", {
          state: {
            selectedPlan,
            appliedCoupon,
            subscriptionData,
            pricingBreakdown,
          },
        });
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "An unexpected error occurred"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handlePayment();
  };

  const total = Math.max(
    0,
    pricingBreakdown?.total ?? selectedPlan.price.amount ?? 0
  );

  return (
    <div className="row my-8">
      <div
        className="card shadow-lg border-0"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderRadius: "20px",
          border: "1px solid rgba(255, 255, 255, 0.2)",
        }}
      >
        <div className="card-body p-4">
          <form onSubmit={handleSubmit}>
            {/* {error && (
              <div
                className="alert alert-danger alert-dismissible fade show mb-4"
                role="alert"
                style={{ borderRadius: "12px", border: "none" }}
              >
                <strong>Error:</strong> {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label="Close"
                />
              </div>
            )} */}

            <div className="mb-4">
              <h6 className="fw-semibold mb-3 text-dark">
                Payment Information
              </h6>
              <PaymentCard stripeError={error} />
            </div>

            <div className="mb-4">
              <TotalDisplay
                selectedPlan={selectedPlan}
                appliedCoupon={appliedCoupon}
                pricingBreakdown={pricingBreakdown}
                total={total}
              />
            </div>

            <div className="mb-4">
              <h6 className="fw-semibold mb-3 text-dark">Have a Coupon?</h6>
              {!appliedCoupon ? (
                <CouponForm
                  onSubmit={handleCouponSubmit}
                  isPending={couponValidation.isPending}
                />
              ) : (
                <AppliedCouponDisplay
                  appliedCoupon={appliedCoupon}
                  pricingBreakdown={pricingBreakdown}
                  selectedPlan={selectedPlan}
                />
              )}
            </div>

            <div className="mb-4">
              <SubmitButton isProcessing={isProcessing} />
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function IntegratedPaymentForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPlan = location.state?.selectedPlan as Subscription;
  const paymentIntent = location.state?.paymentIntent as SetupIntentData;

  useEffect(() => {
    if (!selectedPlan || !paymentIntent) {
      navigate("/Pricing");
    }
  }, [selectedPlan, navigate, paymentIntent]);

  if (!selectedPlan) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center pt-[120px] px-3 pb-2.5 md:pb-0">
      <div className="max-w-xl w-full">
        <div className="text-start mb-6">
          <button
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg hover:bg-white hover:shadow-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => navigate("/Pricing")}
          >
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
              <path
                fillRule="evenodd"
                d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"
              />
            </svg>
            Back to Pricing
          </button>
        </div>

        <Elements stripe={stripePromise}>
          <PaymentFormContent
            selectedPlan={selectedPlan}
            paymentIntent={paymentIntent}
          />
        </Elements>
      </div>
    </div>
  );
}
